import React, { useEffect, useState } from "react";
import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd";
import { turndropType } from "app/data/static-load/model";
import {
  TerminalLocationInfo,
} from "../utils/utils";
// import { ReactComponent as DotIcon } from "images/dots.svg";
import XGSInput from "ui-components/xgs-input/xgsInput";
import "./dropsTable.scss";
import { ReactComponent as DeleteIcon } from "images/trash.svg";

export type DropRow = {
  id: string;
  dropType?: string;
  airportCode?: string;
  terminalName?: string;
  terminalNumber?: string | number;
  miles?: string | number;
  earlyArrivalTime?: string;
  earlyDepartureTime?: string;
  tractorNumber?: string;
};

type DropsTableProps = {
  drops: DropRow[];
  terminalMap: Record<string, string>;
  turnNumber?: string;
  terminalsByAirportCode?: Record<string, TerminalLocationInfo>;
  isEditMode: boolean;
  onSaveDrop: (
    value: string,
    dropId: string,
    type: "tractorNumber" | "earlyArrivalTime" | "earlyDepartureTime"
  ) => void;
  onReorder?: (reorderedDrops: DropRow[]) => void;
};

const HEADER_COLUMNS = [
  { label: "Type", key: "type" },
  { label: "Location", key: "location" },
  { label: "Miles", key: "miles" },
  { label: "Estimated Arrival", key: "arrival" },
  { label: "Estimated Departure", key: "departure" },
  { label: "Tractor", key: "tractor" },
  { label: "Action", key: "action" },
];

const TYPE_BADGE_CLASS: Record<string, string> = {
  O: "origin",
  F: "destination",
  S: "intermediate",
};

const reorder = <T,>(list: T[], startIndex: number, endIndex: number): T[] => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

const formatMiles = (miles?: string | number) => {
  if (miles === undefined || miles === null || miles === "") return "-";
  const value = String(miles);
  return value.toLowerCase().includes("mi") ? value : `${value} mi`;
};



const getTypeLabel = (dropType?: string) =>
  turndropType.find((item) => item.value === dropType)?.label ?? "-";


const DropsTableHeader: React.FC = () => (
  <div className="drops-table__header">
    {HEADER_COLUMNS.map((column) => (
      <div key={column.key} className="drops-table__header-cell">
        {column.label}
      </div>
    ))}
  </div>
);

const formatDropLocation = (
  drop: Pick<DropRow, "airportCode" | "terminalName" | "terminalNumber">
) => {
  const hasTerminalNumber =
    drop.terminalNumber != null &&
    drop.terminalNumber !== "" &&
    Number(drop.terminalNumber) !== 0;

  const parts = [
    drop.airportCode,
    drop.terminalName,
    hasTerminalNumber ? String(drop.terminalNumber) : undefined,
  ].filter((part) => part != null && String(part).trim() !== "");

  return parts.length ? parts.join(" - ") : "-";
};

type DropsTableRowProps = {
  drop: DropRow;
  terminalMap: Record<string, string>;
  turnNumber?: string;
  terminalsByAirportCode?: Record<string, TerminalLocationInfo>;
  isEditMode: boolean;
  onSaveDrop: DropsTableProps["onSaveDrop"];
  isDragging?: boolean;
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement>;
  innerRef?: (element: HTMLElement | null) => void;
  draggableProps?: React.HTMLAttributes<HTMLDivElement>;
  style?: React.CSSProperties;
};

const DropsTableRow: React.FC<DropsTableRowProps> = ({
  drop,
  terminalMap,
  turnNumber,
  terminalsByAirportCode,
  isEditMode,
  onSaveDrop,
  isDragging,
  dragHandleProps,
  innerRef,
  draggableProps,
  style,
}) => {
  const badgeClass = TYPE_BADGE_CLASS[drop.dropType ?? ""] ?? "origin";
  const typeLabel = getTypeLabel(drop.dropType);

  return (
    <div
      ref={innerRef}
      {...draggableProps}
      // {...(isEditMode ? dragHandleProps : {})} // disable drag and drop
      style={style}
      className={[
        "drops-table__row",
        isDragging ? "drops-table__row--dragging" : "",
        // isEditMode ? "drops-table__row--editable" : "", // disable edit mode
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="drops-table__cell">

        {drop.dropType ? (
          <div style={{ display: "flex", alignItems: "center" }}>
            {/* <span style={{ marginRight: "10px", cursor: "grab" }}>
              <DotIcon />
            </span> */}
            <span className={`drops-table__type-badge drops-table__type-badge--${badgeClass}`}>
              {typeLabel}
            </span>
          </div>
        ) : (
          "-"
        )}
      </div>
      <div className="drops-table__cell" title={formatDropLocation(drop)}>
        {formatDropLocation(drop)}
      </div>
      <div className="drops-table__cell">{formatMiles(drop.miles)}</div>
      <div className="drops-table__cell">
        {isEditMode ? (
          <XGSInput
            type="time"
            name={`earlyArrivalTime-${drop.id}`}
            value={drop.earlyArrivalTime ?? ""}
            onChange={(e) => onSaveDrop(e.currentTarget.value, drop.id, "earlyArrivalTime")}
          />
        ) : (
          drop.earlyArrivalTime || "-"
        )}
      </div>
      <div className="drops-table__cell">
        {isEditMode ? (
          <XGSInput
            type="time"
            name={`earlyDepartureTime-${drop.id}`}
            value={drop.earlyDepartureTime ?? ""}
            onChange={(e) => onSaveDrop(e.currentTarget.value, drop.id, "earlyDepartureTime")}
          />
        ) : (
          drop.earlyDepartureTime || "-"
        )}
      </div>
      <div className="drops-table__cell">
        {isEditMode ? (
          <XGSInput
            type="text"
            name={`tractorNumber-${drop.id}`}
            value={drop.tractorNumber ?? ""}
            onChange={(e) => onSaveDrop(e.currentTarget.value, drop.id, "tractorNumber")}
          />
        ) : (
          drop.tractorNumber || "-"
        )}
      </div>
      <div className="drops-table__cell drops-table__cell--action">
        {isEditMode && (
          <div onClick={(e) => { e.stopPropagation(); }}>
            <DeleteIcon className="clone-week-btn__delete_icon" />
          </div>
        )}
      </div>
    </div>
  );
};

const DropsTable: React.FC<DropsTableProps> = ({
  drops,
  terminalMap,
  turnNumber,
  terminalsByAirportCode,
  isEditMode,
  onSaveDrop,
  onReorder,
}) => {
  const [orderedDrops, setOrderedDrops] = useState<DropRow[]>(drops);

  useEffect(() => {
    setOrderedDrops(drops);
  }, [drops]);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination || !isEditMode) return;
    if (result.source.index === result.destination.index) return;

    const reordered = reorder(orderedDrops, result.source.index, result.destination.index);
    setOrderedDrops(reordered);
    onReorder?.(reordered);
  };

  return (
    <div className="drops-table">
      <DropsTableHeader />
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="lane-drops">
          {(provided) => (
            <div
              className="drops-table__body"
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {orderedDrops.length === 0 ? (
                <div className="drops-table__empty">No drops available</div>
              ) : (
                orderedDrops.map((drop, index) => (
                  <Draggable
                    key={drop.id}
                    draggableId={`${"lane-drops"}-${drop.id}`}
                    index={index}
                    isDragDisabled={!isEditMode}
                  >
                    {(draggableProvided, snapshot) => (
                      <DropsTableRow
                        drop={drop}
                        terminalMap={terminalMap}
                        turnNumber={turnNumber}
                        terminalsByAirportCode={terminalsByAirportCode}
                        isEditMode={isEditMode}
                        onSaveDrop={onSaveDrop}
                        isDragging={snapshot.isDragging}
                        innerRef={draggableProvided.innerRef}
                        draggableProps={draggableProvided.draggableProps}
                        dragHandleProps={draggableProvided.dragHandleProps ?? undefined}
                        style={draggableProvided.draggableProps.style}
                      />
                    )}
                  </Draggable>
                ))
              )}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};

export default DropsTable;
